import React, { useState } from 'react'
import { AppDialog, TextInput } from '../Common'
import { useNls } from '../../nls/nlsProvider'
import { validateDevicePin } from '../../types/ops'
import { setMySharedDevicePin } from '../../services/opsSharedDevicesService'

export interface HelperSharedDevicePinModalProps {
  open: boolean
  onClose: () => void
  workspaceId: string
}

function generateOpId(prefix: string): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `${prefix}-${crypto.randomUUID()}`
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function HelperSharedDevicePinModal({
  open,
  onClose,
  workspaceId,
}: HelperSharedDevicePinModalProps): React.ReactElement | null {
  const { t } = useNls()
  const [pin, setPin] = useState('')
  const [confirmPin, setConfirmPin] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [opId, setOpId] = useState(() => generateOpId('set-pin'))

  const handleClose = () => {
    setPin('')
    setConfirmPin('')
    setError(null)
    setSuccessMessage(null)
    setBusy(false)
    onClose()
  }

  const handleSavePin = async () => {
    setError(null)
    setSuccessMessage(null)

    // Check mismatch
    if (pin !== confirmPin) {
      setError(t('NLSOpsSharedDevicePinMismatch'))
      return
    }

    // Client check with functions-common validator re-exported through ops.ts
    const val = validateDevicePin(pin)
    if (!val.valid) {
      setError(val.error || t('NLSOpsSharedDevicePinWeak'))
      return
    }

    setBusy(true)
    try {
      await setMySharedDevicePin({
        workspaceId,
        pin,
        opId,
      })
      setSuccessMessage(t('NLSOpsSharedDevicePinSetSuccess'))
      setOpId(generateOpId('set-pin'))
      setTimeout(() => {
        handleClose()
      }, 1200)
    } catch (err) {
      const msg = err instanceof Error && err.message
        ? err.message
        : t('NLSOpsSharedDevicePinSetFailed')
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppDialog
      open={open}
      onDismiss={handleClose}
      title={t('NLSOpsSharedDeviceSetPinTitle')}
      data-testid="helper-pin-modal"
      actions={[
        {
          label: t('NLSCommonActionCancel'),
          variant: 'secondary',
          onClick: handleClose,
          disabled: busy,
          testId: 'helper-pin-cancel-btn',
        },
        {
          label: t('NLSOpsSave'),
          variant: 'primary',
          onClick: handleSavePin,
          disabled: busy || pin.length !== 4 || confirmPin.length !== 4,
          loading: busy,
          testId: 'helper-pin-save-btn',
        },
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: '260px' }}>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          {t('NLSOpsSharedDeviceSetPinDesc')}
        </p>

        {error && (
          <div
            data-testid="helper-pin-error"
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--danger-subtle)',
              color: 'var(--danger-text)',
              fontSize: '0.875rem',
            }}
          >
            {error}
          </div>
        )}

        {successMessage && (
          <div
            data-testid="helper-pin-success"
            style={{
              padding: '8px 12px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--success-subtle)',
              color: 'var(--success-text)',
              fontSize: '0.875rem',
            }}
          >
            {successMessage}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-muted)' }}>
            {t('NLSOpsSharedDeviceEnterPin')}
          </span>
          <TextInput
            value={pin}
            onChange={(val) => {
              const digits = val.replace(/\D/g, '').slice(0, 4)
              setPin(digits)
              if (error) setError(null)
            }}
            placeholder="••••"
            data-testid="helper-pin-input"
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-muted)' }}>
            {t('NLSOpsSharedDeviceConfirmPin')}
          </span>
          <TextInput
            value={confirmPin}
            onChange={(val) => {
              const digits = val.replace(/\D/g, '').slice(0, 4)
              setConfirmPin(digits)
              if (error) setError(null)
            }}
            placeholder="••••"
            data-testid="helper-pin-confirm-input"
          />
        </div>
      </div>
    </AppDialog>
  )
}
